import axios from 'axios';
import {
  DirectionsRequest,
  DirectionsResponse,
  GeocodeResponse,
  ChatRoom,
  ChatMessage,
  User,
  PublicSystemSettingsResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authService = {
  async login(email: string, password: string) {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  async register(email: string, phoneNumber: string, password: string, first_name: string, last_name: string, tnc_accepted = false, tnc_accepted_at?: string) {
    const response = await api.post('/auth/register', { email, phoneNumber, password, first_name, last_name, tnc_accepted, tnc_accepted_at });
    return response.data;
  },

  async loginWithGoogle(credential: string, tnc_accepted = false) {
    const response = await api.post('/auth/oauth/google', { credential, tnc_accepted });
    return response.data;
  },

  async getProfile(): Promise<{ user: User }> {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  async sendEmailVerification(): Promise<{ status: string; expiresInMinutes?: number }> {
    const response = await api.post('/auth/email/send-verification');
    return response.data;
  },

  async verifyEmail(code: string): Promise<{ status: string; user: User }> {
    const response = await api.post('/auth/email/verify', { code });
    return response.data;
  },

  async forgotPassword(email: string) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  async verifyResetToken(token: string) {
    const response = await api.get(`/auth/verify-reset-token/${token}`);
    return response.data;
  },

  async resetPassword(token: string, password: string, confirmPassword: string) {
    const response = await api.post('/auth/reset-password', {
      token,
      password,
      confirmPassword
    });
    return response.data;
  },
};

// Routes API
export const routesService = {
  async getDirections(request: DirectionsRequest): Promise<DirectionsResponse> {
    const response = await api.post('/routes/directions', request);
    return response.data;
  },

  async geocode(address: string): Promise<GeocodeResponse> {
    const response = await api.post('/routes/geocode', { address });
    return response.data;
  },

  async reverseGeocode(lat: number, lng: number): Promise<GeocodeResponse> {
    const response = await api.post('/routes/reverse-geocode', { lat, lng });
    return response.data;
  },

  async findNearby(lat: number, lng: number, radius?: number, type?: string) {
    const response = await api.post('/routes/nearby', { lat, lng, radius, type });
    return response.data;
  },
};

// Chat API
export const chatService = {
  async getRooms(): Promise<{ rooms: ChatRoom[] }> {
    const response = await api.get('/chat/rooms');
    return response.data;
  },

  async getChatHistory(roomId: string, limit = 50, offset = 0): Promise<{ messages: ChatMessage[]; total: number }> {
    const response = await api.get(`/chat/history/${roomId}?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  async createRoom(name: string, description?: string, isPrivate = false): Promise<{ room: ChatRoom }> {
    const response = await api.post('/chat/rooms', { name, description, isPrivate });
    return response.data;
  },

  async joinRoom(roomId: string, userId: string) {
    const response = await api.post(`/chat/rooms/${roomId}/join`, { userId });
    return response.data;
  },

  async leaveRoom(roomId: string, userId: string) {
    const response = await api.post(`/chat/rooms/${roomId}/leave`, { userId });
    return response.data;
  },
};

// Trips API
export const tripsService = {
  async getMyTrips(role: 'driver' | 'passenger' = 'driver', status?: string) {
    const response = await api.get('/trips/my-trips', { params: { role, status } });
    return response.data;
  },

  async getAvailableTrips(params?: {
    page?: number;
    limit?: number;
    route_id?: string;
    departure_date?: string;
    available_only?: boolean;
  }) {
    const response = await api.get('/trips', { params });
    return response.data;
  },

  async createTrip(tripData: {
    route_id: string;
    departure_datetime: string;
    available_seats: number;
    price_per_seat?: number;
    vehicle_id?: string;
    notes?: string;
    pickup_locations?: any[];
    dropoff_locations?: any[];
  }) {
    const response = await api.post('/trips', tripData);
    return response.data;
  },

  async getTripById(tripId: string) {
    const response = await api.get(`/trips/${tripId}`);
    return response.data;
  },

  async getRecentTrips(limit = 5) {
    const response = await api.get('/trips/my-trips', { params: { limit } });
    return response.data;
  },
};

export const adminTripsService = {
  async getTrips(params?: { page?: number; limit?: number; status?: string; search?: string }) {
    const response = await api.get('/admin/trips', { params });
    return response.data;
  },
};

// Vehicles API
export const vehiclesService = {
  async getMyVehicles() {
    const response = await api.get('/vehicles/my-vehicles');
    return response.data;
  },

  async createVehicle(vehicleData: FormData) {
    const response = await api.post('/vehicles', vehicleData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async updateVehicle(vehicleId: string, vehicleData: FormData) {
    const response = await api.put(`/vehicles/${vehicleId}`, vehicleData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async deleteVehicle(vehicleId: string) {
    const response = await api.delete(`/vehicles/${vehicleId}`);
    return response.data;
  },
};

// Routes API (extended)
export const routesAPIService = {
  async getAllRoutes(params?: {
    page?: number;
    limit?: number;
    include_stops?: boolean;
  }) {
    const response = await api.get('/routes', { params });
    return response.data;
  },

  async getMyRoutes() {
    const response = await api.get('/routes/my-routes');
    return response.data;
  },

  async createRoute(routeData: {
    name: string;
    description?: string;
    start_point: { name: string; latitude: number; longitude: number };
    end_point: { name: string; latitude: number; longitude: number };
    waypoints?: any[];
    is_public?: boolean;
  }) {
    const response = await api.post('/routes', routeData);
    return response.data;
  },

  async getRouteById(routeId: string, includeStops = true) {
    const response = await api.get(`/routes/${routeId}`, { 
      params: { include_stops: includeStops } 
    });
    return response.data;
  },

  async searchRoutes(searchTerm?: string, location?: { lat: number; lng: number; radius?: number }) {
    const params: any = {};
    if (searchTerm) params.q = searchTerm;
    if (location) {
      params.lat = location.lat;
      params.lng = location.lng;
      params.radius = location.radius || 10;
    }
    const response = await api.get('/routes/search', { params });
    return response.data;
  },
};

// Services API
export const servicesService = {
  async getServices(filters?: {
    status?: string;
    category?: string;
    location?: string;
    budget_min?: number;
    budget_max?: number;
    limit?: number;
    offset?: number;
  }) {
    const response = await api.get('/services', { params: filters });
    return response.data;
  },

  async getServiceById(id: string) {
    const response = await api.get(`/services/${id}`);
    return response.data;
  },

  async createService(serviceData: {
    title: string;
    description: string;
    category: string;
    location: string;
    budget: number;
    currency?: string;
    deadline?: string;
    is_urgent?: boolean;
    requirements?: string[];
    attachments?: any[];
  }) {
    const response = await api.post('/services', serviceData);
    return response.data;
  },

  async updateService(id: string, updateData: any) {
    const response = await api.put(`/services/${id}`, updateData);
    return response.data;
  },

  async deleteService(id: string) {
    const response = await api.delete(`/services/${id}`);
    return response.data;
  },

  async getMyServices(filters?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await api.get('/services/my/requests', { params: filters });
    return response.data;
  },

  async acceptProvider(serviceId: string, providerId: string) {
    const response = await api.post(`/services/${serviceId}/accept-provider`, {
      provider_id: providerId
    });
    return response.data;
  },

  async completeService(serviceId: string) {
    const response = await api.post(`/services/${serviceId}/complete`);
    return response.data;
  },

  async cancelService(serviceId: string) {
    const response = await api.post(`/services/${serviceId}/cancel`);
    return response.data;
  },

  async searchServices(term: string, filters?: {
    status?: string;
    category?: string;
    limit?: number;
  }) {
    const response = await api.get(`/services/search/${term}`, { params: filters });
    return response.data;
  },

  async getCategories() {
    const response = await api.get('/services/utils/categories');
    return response.data;
  }
};

// Service Bids API
export const serviceBidsService = {
  async getBidsForService(serviceId: string) {
    const response = await api.get(`/service-bids/service/${serviceId}`);
    return response.data;
  },

  async getMyBids(filters?: {
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await api.get('/service-bids/provider/my-bids', { params: filters });
    return response.data;
  },

  async getBidById(id: string) {
    const response = await api.get(`/service-bids/${id}`);
    return response.data;
  },

  async createBid(bidData: {
    service_id: string;
    bid_amount: number;
    currency?: string;
    estimated_duration?: string;
    proposal: string;
  }) {
    const response = await api.post('/service-bids', bidData);
    return response.data;
  },

  async updateBid(id: string, updateData: any) {
    const response = await api.put(`/service-bids/${id}`, updateData);
    return response.data;
  },

  async acceptBid(id: string) {
    const response = await api.post(`/service-bids/${id}/accept`);
    return response.data;
  },

  async rejectBid(id: string) {
    const response = await api.post(`/service-bids/${id}/reject`);
    return response.data;
  },

  async deleteBid(id: string) {
    const response = await api.delete(`/service-bids/${id}`);
    return response.data;
  },

  async getProviderStats() {
    const response = await api.get('/service-bids/provider/stats');
    return response.data;
  },

  async getServiceStats(serviceId: string) {
    const response = await api.get(`/service-bids/service/${serviceId}/stats`);
    return response.data;
  }
};

// Service Provider Applications API
export const serviceProviderApplicationsService = {
  async getApplications(filters?: {
    status?: string;
    category?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await api.get('/service-provider-applications', { params: filters });
    return response.data;
  },

  async getApplicationById(id: number) {
    const response = await api.get(`/service-provider-applications/${id}`);
    return response.data;
  },

  async getMyApplication() {
    const response = await api.get('/service-provider-applications/user/my-application');
    return response.data;
  },

  async createApplication(formData: FormData) {
    const response = await api.post('/service-provider-applications', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  async updateApplication(id: string, formData: FormData) {
    const response = await api.put(`/service-provider-applications/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  async approveApplication(id: number, comments?: string) {
    const response = await api.post(`/service-provider-applications/${id}/approve`, {
      comments
    });
    return response.data;
  },

  async rejectApplication(id: number, comments?: string) {
    const response = await api.post(`/service-provider-applications/${id}/reject`, {
      comments
    });
    return response.data;
  },

  async deleteApplication(id: string) {
    const response = await api.delete(`/service-provider-applications/${id}`);
    return response.data;
  },

  async searchApplications(term: string, filters?: {
    status?: string;
    limit?: number;
  }) {
    const response = await api.get(`/service-provider-applications/search/${term}`, { params: filters });
    return response.data;
  },

  async getStats() {
    const response = await api.get('/service-provider-applications/utils/stats');
    return response.data;
  }
};

// Service Categories API
export const serviceCategoriesService = {
  async getCategories(includeDeleted = false) {
    const response = await api.get('/service-categories', {
      params: { includeDeleted }
    });
    return response.data;
  },

  async getCategoryById(id: number) {
    const response = await api.get(`/service-categories/${id}`);
    return response.data;
  },

  async createCategory(categoryData: { name: string; description?: string }) {
    const response = await api.post('/service-categories', categoryData);
    return response.data;
  },

  async updateCategory(id: number, categoryData: { name?: string; description?: string }) {
    const response = await api.put(`/service-categories/${id}`, categoryData);
    return response.data;
  },

  async deleteCategory(id: number) {
    const response = await api.delete(`/service-categories/${id}`);
    return response.data;
  },

  async restoreCategory(id: number) {
    const response = await api.post(`/service-categories/${id}/restore`);
    return response.data;
  }
};

// Careers API
export interface CareerPayload {
  position_title: string;
  job_type: 'fulltime' | 'parttime';
  qualifications: string;
  experience?: string;
  location_type: 'onsite' | 'virtual';
  skills_required: string[];
  added_advantage?: string;
  application_deadline: string;
  comments?: string;
  application_email: string;
}

export const careersService = {
  async getOpenCareers() {
    const response = await api.get('/careers');
    return response.data;
  },

  async getAllCareers() {
    const response = await api.get('/careers/admin/all');
    return response.data;
  },

  async createCareer(data: CareerPayload) {
    const response = await api.post('/careers', data);
    return response.data;
  },

  async updateCareer(id: number, data: Partial<CareerPayload>) {
    const response = await api.put(`/careers/${id}`, data);
    return response.data;
  },

  async deleteCareer(id: number) {
    const response = await api.delete(`/careers/${id}`);
    return response.data;
  }
};

// System Settings API (admin only)
export const systemSettingsService = {
  async getPublic(names?: string[]) {
    const params = names && names.length > 0
      ? { names: names.join(',') }
      : undefined;

    const response = await api.get<PublicSystemSettingsResponse>('/system-settings', { params });
    return response.data;
  },

  async getAll() {
    const response = await api.get('/admin/system-settings');
    return response.data;
  },

  async getByName(name: string) {
    const response = await api.get(`/admin/system-settings/${encodeURIComponent(name)}`);
    return response.data;
  },

  async update(name: string, updateData: { value?: string; description?: string | null }) {
    const response = await api.put(`/admin/system-settings/${encodeURIComponent(name)}`, updateData);
    return response.data;
  }
};

export interface AppNotification {
  id: number;
  type: string;
  category: string;
  title: string;
  body: string;
  url: string | null;
  data: Record<string, unknown>;
  read: boolean;
  read_at: string | null;
  created_at: string;
}

export type NotificationChannel = 'in_app' | 'push' | 'email' | 'sms';
export type NotificationPreferenceMatrix = Record<string, Partial<Record<NotificationChannel, boolean>>>;

export interface NotificationPreferencesResponse {
  onboarded: boolean;
  categories: Record<string, { label: string; description: string }>;
  preferences: NotificationPreferenceMatrix;
  hasPhone: boolean;
}

export const notificationsService = {
  async list(params: { limit?: number; before?: number | null; unread_only?: boolean } = {}): Promise<{
    notifications: AppNotification[];
    hasMore: boolean;
    nextCursor: number | null;
    unreadCount: number;
  }> {
    const response = await api.get('/notifications', { params });
    return response.data;
  },

  async markRead(id: number): Promise<{ unreadCount: number }> {
    const response = await api.patch(`/notifications/${id}/read`);
    return response.data;
  },

  async markAllRead() {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },

  async getPreferences(): Promise<NotificationPreferencesResponse> {
    const response = await api.get('/notifications/preferences');
    return response.data;
  },

  async updatePreferences(preferences: NotificationPreferenceMatrix): Promise<NotificationPreferencesResponse> {
    const response = await api.put('/notifications/preferences', { preferences });
    return response.data;
  },

  async dismissOnboarding() {
    await api.post('/notifications/onboarding/dismiss');
  },

  async getPushPublicKey(): Promise<string | null> {
    const response = await api.get('/notifications/push/public-key');
    return response.data.publicKey;
  },

  async subscribePush(subscription: PushSubscriptionJSON) {
    await api.post('/notifications/push/subscribe', { subscription });
  },

  async unsubscribePush(endpoint: string) {
    await api.post('/notifications/push/unsubscribe', { endpoint });
  }
};

export default api;

